import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { authHelper } from '../utils/authHelper';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminCourseEditor() {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState({});
  const [exams, setExams] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [showExamModal, setShowExamModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  
  // Forms
  const [moduleForm, setModuleForm] = useState({
    title: '', description: '', order: 1, unlock_week: 1
  });
  const [lessonForm, setLessonForm] = useState({
    title: '', description: '', order: 1, video_url: '', duration: 0
  });

  useEffect(() => {
    loadCourseData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadCourseData = async () => {
    try {
      // Load course
      const courseRes = await fetch(`${API}/api/admin/lms/courses`, {
        headers: authHelper.getAuthHeaders()
      });
      const courseData = await courseRes.json();
      const foundCourse = courseData.courses.find(c => c.course_id === courseId);
      setCourse(foundCourse);

      // Load modules
      const modulesRes = await fetch(`${API}/api/lms/courses/${courseId}`, {
        headers: authHelper.getAuthHeaders()
      });
      const moduleData = await modulesRes.json();
      setModules(moduleData.modules || []);

      // Load lessons for each module
      const lessonsMap = {};
      for (const module of moduleData.modules || []) {
        const lessonsRes = await fetch(`${API}/api/lms/modules/${module.module_id}/lessons`, {
          headers: authHelper.getAuthHeaders()
        });
        const lessonData = await lessonsRes.json();
        lessonsMap[module.module_id] = lessonData.lessons || [];
      }
      setLessons(lessonsMap);

    } catch (err) {
      console.error('Error loading course data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/lms/modules`, {
        method: 'POST',
        headers: {
          ...authHelper.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...moduleForm,
          course_id: courseId
        })
      });
      
      if (res.ok) {
        await loadCourseData();
        setShowModuleModal(false);
        setModuleForm({ title: '', description: '', order: 1, unlock_week: 1 });
      }
    } catch (err) {
      console.error('Error creating module:', err);
    }
  };

  const handleCreateLesson = async (e) => {
    e.preventDefault();
    if (!selectedModule) return;

    try {
      const res = await fetch(`${API}/api/admin/lms/lessons`, {
        method: 'POST',
        headers: {
          ...authHelper.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...lessonForm,
          module_id: selectedModule
        })
      });
      
      if (res.ok) {
        await loadCourseData();
        setShowLessonModal(false);
        setLessonForm({ title: '', description: '', order: 1, video_url: '', duration: 0 });
        setSelectedModule(null);
      }
    } catch (err) {
      console.error('Error creating lesson:', err);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('¿Eliminar este módulo y todas sus lecciones?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/lms/modules/${moduleId}`, {
        method: 'DELETE',
        headers: authHelper.getAuthHeaders()
      });
      
      if (res.ok) {
        await loadCourseData();
      }
    } catch (err) {
      console.error('Error deleting module:', err);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('¿Eliminar esta lección?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/lms/lessons/${lessonId}`, {
        method: 'DELETE',
        headers: authHelper.getAuthHeaders()
      });
      
      if (res.ok) {
        await loadCourseData();
      }
    } catch (err) {
      console.error('Error deleting lesson:', err);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-white">Cargando...</div>;
  }

  if (!course) {
    return <div className="p-8 text-center text-white">Curso no encontrado</div>;
  }

  return (
    <div className="p-6 lg:p-8 bg-[#02040A] min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="text-[#C5A14C] hover:underline mb-4"
          >
            ← Volver a cursos
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">{course.title}</h1>
          <p className="text-gray-400">{course.description}</p>
        </div>

        {/* Add Module Button */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">Módulos del Curso</h2>
          <button
            onClick={() => {
              setModuleForm({ title: '', description: '', order: modules.length + 1, unlock_week: 1 });
              setShowModuleModal(true);
            }}
            className="px-4 py-2 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
          >
            + Agregar Módulo
          </button>
        </div>

        {/* Modules List */}
        {modules.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <p className="text-gray-400 mb-4">No hay módulos todavía</p>
            <button
              onClick={() => setShowModuleModal(true)}
              className="px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
            >
              Crear primer módulo
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {modules.map(module => (
              <div key={module.module_id} className="bg-gray-800 rounded-lg border border-gray-700">
                {/* Module Header */}
                <div className="p-5 border-b border-gray-700">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">
                        Módulo {module.order}: {module.title}
                      </h3>
                      <p className="text-gray-400 text-sm">{module.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-blue-900 text-blue-300 rounded text-sm">
                        Semana {module.unlock_week}
                      </span>
                      <button
                        onClick={() => handleDeleteModule(module.module_id)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>

                {/* Lessons */}
                <div className="p-5">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-semibold text-white">Lecciones</h4>
                    <button
                      onClick={() => {
                        setSelectedModule(module.module_id);
                        setLessonForm({ 
                          title: '', 
                          description: '', 
                          order: (lessons[module.module_id]?.length || 0) + 1, 
                          video_url: '', 
                          duration: 0 
                        });
                        setShowLessonModal(true);
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                    >
                      + Agregar Lección
                    </button>
                  </div>

                  {(!lessons[module.module_id] || lessons[module.module_id].length === 0) ? (
                    <p className="text-gray-500 text-sm">No hay lecciones todavía</p>
                  ) : (
                    <div className="space-y-3">
                      {lessons[module.module_id].map(lesson => (
                        <div key={lesson.lesson_id} className="bg-gray-900 rounded p-4 flex justify-between items-center">
                          <div className="flex-1">
                            <h5 className="text-white font-semibold mb-1">
                              {lesson.order}. {lesson.title}
                            </h5>
                            <p className="text-gray-400 text-sm mb-2">{lesson.description}</p>
                            <div className="flex gap-4 text-xs text-gray-500">
                              <span>🎥 {Math.floor(lesson.duration / 60)}:{(lesson.duration % 60).toString().padStart(2, '0')} min</span>
                              <span className="truncate max-w-xs">{lesson.video_url}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteLesson(lesson.lesson_id)}
                            className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Module Modal */}
        {showModuleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Crear Módulo</h2>
              <form onSubmit={handleCreateModule}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-300 mb-2">Orden *</label>
                      <input
                        type="number"
                        required
                        min="1"
                        value={moduleForm.order}
                        onChange={(e) => setModuleForm({...moduleForm, order: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 mb-2">Desbloquear en Semana *</label>
                      <input
                        type="number"
                        required
                        min="1"
                        value={moduleForm.unlock_week}
                        onChange={(e) => setModuleForm({...moduleForm, unlock_week: parseInt(e.target.value)})}
                        className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Título del Módulo *</label>
                    <input
                      type="text"
                      required
                      value={moduleForm.title}
                      onChange={(e) => setModuleForm({...moduleForm, title: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Ej: Fundamentos del Trading"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Descripción *</label>
                    <textarea
                      required
                      rows="3"
                      value={moduleForm.description}
                      onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Describe el contenido del módulo..."
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModuleModal(false)}
                    className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
                  >
                    Crear Módulo
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Lesson Modal */}
        {showLessonModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Crear Lección</h2>
              <form onSubmit={handleCreateLesson}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Orden *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={lessonForm.order}
                      onChange={(e) => setLessonForm({...lessonForm, order: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Título de la Lección *</label>
                    <input
                      type="text"
                      required
                      value={lessonForm.title}
                      onChange={(e) => setLessonForm({...lessonForm, title: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Ej: ¿Qué es el trading?"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Descripción *</label>
                    <textarea
                      required
                      rows="3"
                      value={lessonForm.description}
                      onChange={(e) => setLessonForm({...lessonForm, description: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Describe el contenido de la lección..."
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">URL del Video *</label>
                    <input
                      type="text"
                      required
                      value={lessonForm.video_url}
                      onChange={(e) => setLessonForm({...lessonForm, video_url: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="/uploads/videos/lesson-001.mp4 o URL externa"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Duración (segundos)</label>
                    <input
                      type="number"
                      min="0"
                      value={lessonForm.duration}
                      onChange={(e) => setLessonForm({...lessonForm, duration: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Ej: 900 (15 minutos)"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {setShowLessonModal(false); setSelectedModule(null);}}
                    className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
                  >
                    Crear Lección
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCourseEditor;
